import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Activity, Dumbbell, Brain, HeartPulse, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { TREATMENTS, CATEGORIES } from "@/lib/site";

const ICONS = {
  pain: Activity,
  sports: Dumbbell,
  neuro: Brain,
  postop: HeartPulse,
  advanced: Sparkles,
};

export default function Treatments() {
  const [active, setActive] = useState("all");
  const list = active === "all" ? TREATMENTS : TREATMENTS.filter((t) => t.cat === active);
  const catOf = (id) => CATEGORIES.find((c) => c.id === id);

  return (
    <div data-testid="treatments-page" className="pt-[72px]">
      <Seo
        title="Treatments | Physiotherapy, Sports & Neuro Rehab in Gomti Nagar, Lucknow"
        description="23 specialised physiotherapy treatments in Gomti Nagar, Lucknow — pain management, sports rehab, stroke rehabilitation, post-surgical recovery, dry needling and chiropractic care."
      />

      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <p className="mono-label text-gold">Treatments &amp; Therapies</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            23 ways we put movement <span className="italic text-gold">back in your hands.</span>
          </h1>
          <p className="mt-6 text-alabaster/70 max-w-2xl leading-relaxed">
            Every treatment below is delivered one-on-one by certified hands — grouped into five
            clinical programmes so you know exactly where your recovery begins.
          </p>
        </div>
      </section>

      {/* CATEGORY TABS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <div className="flex flex-wrap gap-2.5 sticky top-[84px] z-30 bg-alabaster/90 backdrop-blur-xl py-3 -mx-2 px-2">
          <button
            data-testid="treatments-tab-all"
            onClick={() => setActive("all")}
            className={`text-xs sm:text-sm font-semibold rounded-full px-5 py-2.5 border transition-all duration-200 ${
              active === "all"
                ? "bg-teal-brand text-alabaster border-teal-brand"
                : "bg-white text-inkmuted border-teal-brand/15 hover:border-teal-brand/50"
            }`}
          >
            All Treatments
          </button>
          {CATEGORIES.map((c) => {
            const Icon = ICONS[c.id];
            return (
              <button
                key={c.id}
                data-testid={`treatments-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`inline-flex items-center gap-2 text-xs sm:text-sm font-semibold rounded-full px-5 py-2.5 border transition-all duration-200 ${
                  active === c.id
                    ? "bg-teal-brand text-alabaster border-teal-brand"
                    : "bg-white text-inkmuted border-teal-brand/15 hover:border-teal-brand/50"
                }`}
              >
                <Icon size={14} /> {c.label}
              </button>
            );
          })}
        </div>

        {active !== "all" && (
          <Reveal key={active}>
            <p className="mt-8 font-display italic text-2xl text-teal-brand">{catOf(active)?.blurb}</p>
          </Reveal>
        )}

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {list.map((t, i) => {
              const cat = catOf(t.cat);
              const Icon = ICONS[t.cat];
              return (
                <motion.div
                  key={t.name}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.45, delay: (i % 6) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full"
                >
                  <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-7 card-lift flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="h-10 w-10 rounded-full bg-teal-soft text-teal-brand flex items-center justify-center">
                        <Icon size={17} />
                      </span>
                      <span className="mono-label text-gold-deep !text-[9px]">{cat?.label}</span>
                    </div>
                    <h3 className="font-display text-xl lg:text-2xl font-semibold text-teal-brand mt-4 leading-snug">
                      {t.name}
                    </h3>
                    <p className="text-inkmuted text-sm leading-relaxed mt-3 flex-1">{t.desc}</p>
                    <Link
                      to="/book-appointment"
                      data-testid={`treatment-book-${t.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-brand hover:text-gold-deep transition-colors"
                    >
                      Book this treatment <ArrowRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-soft/60 border-t border-teal-brand/10">
        <div className="max-w-4xl mx-auto px-5 py-20 text-center">
          <SectionHead align="center" label="Not Sure Where to Start?" title="Start With One Assessment" />
          <Reveal delay={0.15}>
            <p className="mt-5 text-inkmuted max-w-xl mx-auto leading-relaxed">
              One 45-minute clinical assessment identifies the true cause of your pain — and maps
              the shortest route out of it.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/book-appointment"
              data-testid="treatments-book-cta"
              className="mt-8 inline-block bg-teal-brand text-alabaster font-semibold rounded-full px-8 py-4 text-sm hover:bg-teal-deep transition-colors duration-300"
            >
              Book Your Assessment
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
